import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AccountCircleFill,
  Notifications,
  Watch,
} from "@material-symbols-svg/react";
import { toast } from "sonner";

import ConnectedBottomSheet from "src/components/app/bottom-sheet/devices/ConnectedBottomSheet";
import AvailableDeviceBottomSheet from "src/components/app/bottom-sheet/devices/AvailableDeviceBottomSheet";
import { sendMessageToParent, sendStatusBarColor } from "src/helpers/common";
import { useAppSelector } from "src/redux/hooks";
import { POST_MESSAGE_EVENTS } from "src/constants/common";
import supportPrograms from "src/data/app/programs";

interface HomeHeaderProps {
  supportProgramKey: string;
}

// Scroll distance (px) into the scroll container before the header switches
// from a transparent overlay to the pinned, glassy state.
const SCROLL_THRESHOLD = 24;

// Matches the standard app Header (src/components/app/layout/Header.tsx:
// py-2 + w-10 h-10 icon buttons ≈ 57px) so the pinned state lines up with
// other screens' fixed headers, e.g. RecordsScreen.
const STICKY_HEADER_HEIGHT = 57;

const HomeHeader: React.FC<HomeHeaderProps> = ({ supportProgramKey }) => {
  const navigate = useNavigate();
  const patient = useAppSelector((state) => state.auth.patient);
  const cgm = useAppSelector((state) => state.cgm);
  const { connectedDevices } = useAppSelector((state) => state.device);
  const { statusAndSensorLife } = useAppSelector((state) => state.cgm);
  const hasCgmDevice =
    connectedDevices?.data?.some(
      (d: { id: string }) => d.id === "goodflip-cgm-reader",
    ) ?? !!cgm.boundDevice;
  const isCgmSyncing =
    hasCgmDevice &&
    (cgm.cgmHardSync?.isLoading ||
      cgm.cgmGetLatest?.isLoading ||
      cgm.cgmMissingSync?.isLoading);

  const [showConnectedBottomSheet, setShowConnectedBottomSheet] =
    useState(false);
  const [showAvailableDeviceBottomSheet, setShowAvailableDeviceBottomSheet] =
    useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // The page scrolls inside #scrollableTarget (see DynamicScreen.tsx), not
  // the window, so we listen there rather than on `window`.
  useEffect(() => {
    const scrollContainer = document.getElementById("scrollableTarget");
    if (!scrollContainer) return;

    const handleScroll = () => {
      setIsScrolled(scrollContainer.scrollTop > SCROLL_THRESHOLD);
    };

    handleScroll();
    scrollContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  // TEST WIRING: while this header is mounted (i.e. the hero is on screen),
  // drive the native status bar off the HeroBanner background — the
  // program's primary color at rest, white once scrolled to the glassy
  // state. Only this screen is wired up for now.
  useEffect(() => {
    const primaryColor =
      supportPrograms[supportProgramKey as keyof typeof supportPrograms]
        ?.theme?.colors?.primary ?? "#ffffff";

    return sendStatusBarColor(
      isScrolled ? "#ffffff" : primaryColor,
      isScrolled ? "dark-content" : "light-content",
    );
  }, [isScrolled, supportProgramKey]);

  const openConnectionFlow = useCallback(
    (deviceId: string) => {
      setShowConnectedBottomSheet(false);
      setShowAvailableDeviceBottomSheet(false);
      if (deviceId === "goodflip-smart-scale") {
        navigate(
          `/app/${supportProgramKey}/smart-scale-connection?deviceId=${deviceId}`,
        );
      } else if (deviceId === "goodflip-cgm-reader") {
        const cgmStatusData = statusAndSensorLife?.data?.[0] ?? null;
        if (cgmStatusData) {
          navigate(`/app/${supportProgramKey}/cgm-status`);
        } else {
          navigate(`/app/${supportProgramKey}/cgm-connection`);
        }
      } else {
        console.error("Invalid device ID");
        toast.error("Invalid device ID");
      }
    },
    [navigate, supportProgramKey, statusAndSensorLife],
  );

  const handleAvatarClick = () => {
    navigate(`/app/${supportProgramKey}/profile-screen`);
  };

  const handleNotificationClick = () => {
    sendMessageToParent(POST_MESSAGE_EVENTS.NAVIGATE_TO_NOTIFICATIONS);
  };

  const handleConnectedClick = () => {
    if (connectedDevices?.data?.length === 0) {
      setShowAvailableDeviceBottomSheet(true);
      return;
    }
    setShowConnectedBottomSheet(true);
  };

  return (
    <div
      className={`${
        isScrolled ? "fixed" : "absolute"
      } top-0 left-0 right-0 z-20 max-w-lg mx-auto transition-colors duration-300`}
      style={
        isScrolled
          ? {
              height: STICKY_HEADER_HEIGHT,
              backgroundColor: "rgba(255, 255, 255, 0.65)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
            }
          : { backgroundColor: "transparent" }
      }
    >
      <div
        className={`flex items-center justify-between px-3 ${
          isScrolled ? "h-full" : "py-1.5"
        }`}
      >
        {/* Avatar */}
        <button
          className="relative w-8 h-8 rounded-full shadow-md hover:shadow-lg transition-shadow bg-white border-2 border-white flex items-center justify-center overflow-hidden"
          onClick={handleAvatarClick}
        >
          <span className="font-semibold text-[var(--app-primary)] text-xs">
            {patient?.data?.name ? (
              patient?.data?.name?.charAt(0).toUpperCase()
            ) : (
              <AccountCircleFill className="w-5 h-5 text-[var(--app-primary)]" />
            )}
          </span>
        </button>

        {/* Device + Notifications */}
        <div className="flex items-center gap-2">
          <button
            className="relative w-8 h-8 rounded-full shadow-md hover:shadow-lg transition-shadow flex items-center justify-center overflow-hidden"
            onClick={handleConnectedClick}
          >
            {isCgmSyncing ? (
              <>
                <div
                  className="absolute inset-0 rounded-full animate-spin"
                  style={{
                    background:
                      "conic-gradient(from 0deg, #ffffff, #ffffff, var(--app-primary))",
                  }}
                />
                <div className="absolute inset-[2px] rounded-full bg-white flex items-center justify-center shadow-md z-10">
                  <Watch className="w-5 h-5 text-gray-700" />
                </div>
              </>
            ) : (
              <div className="w-full h-full bg-white hover:bg-gray-50 rounded-full shadow-md hover:shadow-lg flex items-center justify-center">
                <Watch className="w-5 h-5 text-gray-700" />
              </div>
            )}
          </button>

          <button
            className="relative w-8 h-8 rounded-full shadow-md hover:shadow-lg transition-shadow bg-white flex items-center justify-center overflow-hidden"
            onClick={handleNotificationClick}
          >
            <Notifications />
          </button>
        </div>
      </div>

      {showConnectedBottomSheet && (
        <ConnectedBottomSheet
          show={showConnectedBottomSheet}
          setShow={setShowConnectedBottomSheet}
          onAddDeviceClick={() => setShowAvailableDeviceBottomSheet(true)}
          startConnectionFlow={(deviceId: string) => {
            setShowConnectedBottomSheet(false);
            openConnectionFlow(deviceId);
          }}
        />
      )}

      {showAvailableDeviceBottomSheet && (
        <AvailableDeviceBottomSheet
          show={showAvailableDeviceBottomSheet}
          setShow={setShowAvailableDeviceBottomSheet}
          startConnectionFlow={(deviceId: string) => {
            setShowAvailableDeviceBottomSheet(false);
            openConnectionFlow(deviceId);
          }}
        />
      )}
    </div>
  );
};

export default HomeHeader;
